import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { AlertType, TextButton } from '../../components';
import { useHandleApiResponse, useToast } from '../../hooks';
import { useGenerateIntegrationOauthStateMutation } from '../../services';
import { IntegrationConnectionStatus, IntegrationService } from '../../types';

type ConnectOAuthButtonProps = {
  service: IntegrationService;
  authorizationUrl: string;
  disabled: boolean;
  setConnectionStatus: Dispatch<SetStateAction<IntegrationConnectionStatus>>;
};

const ConnectOAuthButton = ({ service, authorizationUrl, disabled, setConnectionStatus }: ConnectOAuthButtonProps) => {
  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);

  const [generateIntegrationOauthState] = useGenerateIntegrationOauthStateMutation();

  const onError = useCallback(() => {
    setConnectionStatus(IntegrationConnectionStatus.FAILED);
    setIsConnecting(false);
  }, [setConnectionStatus]);

  // Handles the OAuth connection by generating the OAuth state and opening the authorization URL in a new tab.
  const handleOauthConnect = useCallback(async () => {
    try {
      setIsConnecting(true);
      const response = await generateIntegrationOauthState({ service });
      handleApiResponse({
        response,
        onError,
        hideSuccessToast: true, // We do not need the success toast because if the API is successful, it navigates to OAuth page.
        onSuccess: ({ state }) => {
          // Redirect to the authorization URL in the current tab.
          // This prevents multiple tabs from opening since integration's OAuth flow
          // will redirect back to our application after the user approves or denies access.
          window.location.href = `${authorizationUrl}&state=${state}`;
          // isConnecting will remain true until the page redirects
        },
      });
    } catch (error) {
      onError();
      showToast({ message: 'Something went wrong. Please try again later.', type: AlertType.ERROR });
      console.error('OAuth process failed: ', error);
    }
  }, [generateIntegrationOauthState, service, authorizationUrl, onError, handleApiResponse, showToast]);

  return <TextButton disabled={disabled} text="Connect" onClick={handleOauthConnect} loading={isConnecting} />;
};

export default ConnectOAuthButton;
